/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import Button, {ButtonProps} from '@material-ui/core/Button';
import {alpha, makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import {dlsColor, dlsDarkAlpha, dlsShadow, isDarkPalette} from '@ion/dls/src/theme/theme';
import {DLSShadowStyle, DLSTheme} from '../../../types/types';
import {StableButtonLabel} from './StableButtonLabel';

interface TertiaryButtonProps extends ButtonProps {
  children: ReactNode;
};

const iconStyles = {
  '& .MuiButton-root' : {
    height       : '32px !important',
    borderRadius : '8px !important'
  }
};

// the pressed border's alpha, 0x1F out of 0xFF ...
const PRESSED_BORDER_ALPHA = 0x1F / 255;

// the pressed border's veil, the darkest blue token in dark mode, otherwise the
// light blue, both at the pressed alpha ...
const PRESSED_BORDER_VEIL = {
  alphaValue : PRESSED_BORDER_ALPHA,
  lightValue : alpha('#0863AA', PRESSED_BORDER_ALPHA)
};

const getPressedBorder = (isDark: boolean): string => (
  `1px solid ${dlsDarkAlpha(isDark, 'darkestBlue', PRESSED_BORDER_VEIL)}`
);

const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    padding : '3px'
  },
  root : {
    gap           : '8px',
    color         : dlsColor('darkestBlue'),
    height        : '32px',
    display       : 'flex',
    padding       : '0px 16px',
    fontSize      : '13px',
    minHeight     : '32px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '14px !important',
    borderRadius  : '8px !important',
    textTransform : 'capitalize !important' as any,
    letterSpacing : '0.5px',
    ...iconStyles,
    '&:hover'     : {
      boxShadow       : dlsShadow(3, DLSShadowStyle.DropShadow),
      borderRadius    : '8px !important',
      backgroundColor : dlsColor('aquamarine')
    },
    '&:active' : {
      border          : getPressedBorder(isDarkPalette(theme)),
      boxShadow       : 'none',
      backgroundColor : dlsColor('aquamarine')
    },
    '&:focus' : {
      outline : 'none'
    },
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('skyBlue')}`,
      borderRadius  : '8px !important',
      outlineOffset : '2px'
    },
    '&$disabled' : {
      color : dlsColor('grey4')
    }
  }
}));

/**
 * Tertiary buttons can be paired with primary buttons, secondary buttons,
 * or other tertiary buttons.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <TertiaryButton>Default</TertiaryButton>
 *       <TertiaryButton noUnderline>Default without underline</TertiaryButton>
 *       <br />
 *       <TertiaryButton disabled>Disabled</TertiaryButton>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
export const TertiaryButton = ({
  classes: customClasses,
  ...props
}: TertiaryButtonProps) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <Button disableRipple disableFocusRipple
        classes={{
          root : clsx(classes.root, customClasses?.root)
        }}
        {...props}>
        <StableButtonLabel>{props.children}</StableButtonLabel>
      </Button>
    </div>
  );
};

export default TertiaryButton;
